import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserProfileContext } from '../../../context/userProfileContext';
import { getClientProfilePolicies } from '../../../utils/api/insurancePolicy';
import { InsurancePolicy, InsurancePolicyMainBranch } from '../../../interfaces/InsurancePolicy';
import { Client } from '../../../interfaces/Client';
import Table from '../../../components/Table';
import Loader from '../../../components/Loader';
import globalErrorHandler from '../../../utils/globalErrorHandler';
import { camelCaseToText } from '../../../utils/camelCaseToText';
import { InsuranceMiniPreferredTypes } from '../../../interfaces/Insurance';
import { formatDate } from '../../../utils/formatDate';
import { Company } from '../../../interfaces/Company';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ClientFile } from '../../../interfaces/ClientFile';
import { addCommas } from '../../../utils/addCommas';
import './insurance.scss';

/**
 * A read-only view of client insurance policies displayed when there are no active insurance rounds.
 * Fetches policies directly by clientId using the dedicated client-profile endpoint.
 * Also displays insurancePreferredTypes from the latest insurance round.
 */
const ClientPoliciesReadOnly = () => {
    const { t: t1 } = useTranslation('insurance');
    const { t: t2 } = useTranslation('insurancePolicies');
    const t = (key: string) => (t1(key) !== key ? t1(key) : t2(key));

    const { profileContextData } = useContext(UserProfileContext);
    const { client } = profileContextData;

    const [policies, setPolicies] = useState<InsurancePolicy[]>([]);
    const [insurancePreferredTypes, setInsurancePreferredTypes] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    useEffect(() => {
        const fetchPolicies = async () => {
            if (!client?._id) return;

            setIsLoading(true);
            try {
                const branches = [
                    InsurancePolicyMainBranch.healthInsurance,
                    InsurancePolicyMainBranch.lifeInsurance,
                    InsurancePolicyMainBranch.longTermCare,
                    InsurancePolicyMainBranch.workDisability,
                ];

                const response = await getClientProfilePolicies({
                    clientId: client._id,
                    branch: branches.join(','),
                });

                const fetchedPolicies = response.policies || [];
                fetchedPolicies.forEach((policy: InsurancePolicy) => {
                    policy.clientId = typeof policy.client === 'string' ? policy.client : (policy.client as Client)._id;
                });
                setPolicies(fetchedPolicies);
                setInsurancePreferredTypes(response.insurancePreferredTypes || []);
            } catch (error) {
                globalErrorHandler(error);
            } finally {
                setIsLoading(false);
                setHasLoaded(true);
            }
        };

        fetchPolicies();
    }, [client?._id]);

    // Column components for read-only display
    const PolicyMainBranchColumn = ({ row: { original: policy } }: { row: { original: InsurancePolicy } }) => {
        if (!policy.branch) return <>---</>;

        let text =
            policy.branch === InsurancePolicyMainBranch.healthInsurance && policy.appendices?.length === 1
                ? policy.appendices[0].branch
                : t(camelCaseToText(policy.branch));

        return (
            <div>
                {text}
                <br />
                {t(policy.planType)}
            </div>
        );
    };

    const PolicyCompanyNameColumn = ({ row: { original: policy } }: { row: { original: InsurancePolicy } }) => {
        if (!policy.company) return <>---</>;
        const company = policy.company as Company;
        if (company.thumbUrl) {
            return <img src={company.thumbUrl} alt={company.shortName || company.name} style={{ maxHeight: '60px', maxWidth: '150px' }} />;
        }
        return <>{company.shortName || company.name}</>;
    };

    const PolicyNumberColumn = ({ row: { original: policy } }: { row: { original: InsurancePolicy } }) => {
        const file: ClientFile | undefined = policy.policyFile || policy.annualReports?.[0];
        return file ? (
            <a href={file.openLink || file.link} target="_blank" rel="noreferrer">
                {policy.policyNumber} <i className="mdi mdi-link"></i>
            </a>
        ) : (
            <>{policy.policyNumber}</>
        );
    };

    const PolicyInsuredListColumn = ({ row: { original: policy } }: { row: { original: InsurancePolicy } }) => {
        return <>{policy.insuredList?.map((person, idx) => <div key={idx}>{person.name}</div>)}</>;
    };

    const PolicyPremiumColumn = ({ row: { original: policy } }: { row: { original: InsurancePolicy } }) => {
        const coverageAppendix = policy.appendices?.find(
            (appendix) => appendix.riskCoverageAmount || appendix.cancerCoverageAmount || appendix.criticalIllnessCoverageAmount,
        );
        const coverageAmount =
            coverageAppendix?.riskCoverageAmount || coverageAppendix?.cancerCoverageAmount || coverageAppendix?.criticalIllnessCoverageAmount;
        if (!Number.isFinite(policy.totalPremium) && !Number.isFinite(coverageAmount)) {
            return <>---</>;
        }
        return (
            <>
                ₪{addCommas(policy.totalPremium)}{' '}
                {policy.premiumDiscountPercentage ? (
                    <span className="text-success">
                        ({t('Discount')} {policy.premiumDiscountPercentage}%)
                    </span>
                ) : null}
                {coverageAmount ? <div className="text">(₪{addCommas(coverageAmount)})</div> : null}
            </>
        );
    };

    const PolicyTermColumn = ({ row: { original: policy } }: { row: { original: InsurancePolicy } }) => {
        if (policy.insuranceStartDate && policy.insuranceEndDate) {
            return (
                <OverlayTrigger
                    placement="bottom"
                    overlay={
                        <Tooltip id="">
                            {formatDate(new Date(policy.insuranceStartDate), true)} - {formatDate(new Date(policy.insuranceEndDate), true)}
                        </Tooltip>
                    }
                >
                    <span className="cursor-pointer">
                        {new Date(policy.insuranceEndDate).getFullYear()} - {new Date(policy.insuranceStartDate).getFullYear()}
                    </span>
                </OverlayTrigger>
            );
        } else if (policy.insuranceStartDate) {
            return <>{formatDate(new Date(policy.insuranceStartDate), true)} - </>;
        } else if (policy.insuranceEndDate) {
            return <>- {formatDate(new Date(policy.insuranceEndDate), true)}</>;
        }
        return <></>;
    };

    const PolicyRecommendationColumn = ({ row: { original: policy } }: { row: { original: InsurancePolicy } }) => {
        if (policy.isNewPolicy) return <>{t('New')}</>;
        if (!policy.recommendation) return <>---</>;
        return <>{t(camelCaseToText(policy.recommendation))}</>;
    };

    const PolicyStatusColumn = ({ row: { original: policy } }: { row: { original: InsurancePolicy } }) => {
        if (!policy.status) return <>---</>;
        return <>{t(camelCaseToText(policy.status))}</>;
    };

    const policyColumns = [
        {
            Header: t('Main Branch'),
            accessor: 'branch',
            Cell: PolicyMainBranchColumn,
        },
        {
            Header: t('Company'),
            accessor: 'company',
            Cell: PolicyCompanyNameColumn,
        },
        {
            Header: t('Policy Number'),
            accessor: 'policyNumber',
            Cell: PolicyNumberColumn,
        },
        {
            Header: t('Persons'),
            accessor: 'insuredList',
            Cell: PolicyInsuredListColumn,
        },
        {
            Header: t('Premium'),
            accessor: 'totalPremium',
            Cell: PolicyPremiumColumn,
        },
        {
            Header: t('Term'),
            accessor: 'startDate',
            Cell: PolicyTermColumn,
        },
        {
            Header: t('Recommendation'),
            accessor: 'recommendation',
            Cell: PolicyRecommendationColumn,
        },
        {
            Header: t('Status'),
            accessor: 'status',
            Cell: PolicyStatusColumn,
        },
    ];

    const rowStyle = (row: any) => {
        const policy = row.original;
        const styles: any = { height: '70px' };

        if (policy.isNewPolicy) {
            styles.backgroundColor = 'rgb(198, 236, 227, 0.65)';
        }

        return styles;
    };

    // Don't render anything if still loading or no policies found
    if (!hasLoaded) {
        return isLoading ? <Loader /> : null;
    }

    if (policies.length === 0) {
        return null;
    }

    return (
        <div className="client-policies-readonly card-shadow mt-3">
            <div className="policies-header p-3">
                <h5 className="mb-0">{t('Existing Policies')}</h5>
            </div>

            {insurancePreferredTypes.length > 0 && (
                <div className="badges px-3 pb-2">
                    {insurancePreferredTypes.map((preferredType, idx) => (
                        <span key={idx} className={`badge ${InsuranceMiniPreferredTypes.includes(preferredType) ? 'bg-warning' : 'bg-success'} me-1`}>
                            {camelCaseToText(preferredType)}
                        </span>
                    ))}
                </div>
            )}

            <Table columns={policyColumns} data={policies} pagination={false} tableClass="table-nowrap products-table" rowStyle={rowStyle} />
        </div>
    );
};

export default ClientPoliciesReadOnly;
